import { AnimatePresence, motion } from 'framer-motion'
import React, { HTMLAttributes } from 'react'
import { FaCheckCircle, FaRegCircle } from 'react-icons/fa'
import styled from 'styled-components'

export const RadioGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RadioList role="radio-group" aria-required>
      <AnimatePresence>{children}</AnimatePresence>
    </RadioList>
  )
}

export const RadioList = styled(motion.div).attrs({
  initial: 'hide',
  animate: 'show',
  variants: {
    show: {
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.05
      }
    },
    hide: {
      transition: { staggerChildren: 0.05, staggerDirection: -1 }
    }
  },
  transition: {
    staggerChildren: 0.1,
    delayChildren: 0.1
  }
})`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const RadioButtonText = styled.div<{ $isSelected: boolean }>`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.neutralBlack : theme.colors.metalGray};
`

const RadioButtonSuffix = styled.span<{ $intent: 'default' | 'primary' }>`
  font-weight: ${({ $intent }) => ($intent === 'primary' ? 600 : 500)};
  font-size: ${({ theme, $intent }) =>
    $intent === 'primary' ? theme.fontSize.xs : theme.fontSize.sm}rem;
  letter-spacing: ${({ $intent }) => ($intent === 'primary' ? 0 : '1px')};
  color: ${({ theme, $intent }) =>
    $intent === 'primary' ? theme.colors.primary : theme.colors.iconGray};
`

const RadioButtonBody = styled.div`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  margin: 0;
  margin-right: 2em;
  margin-top: 0.5rem;
`

type RadioValue = any

interface RadioItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  index?: number
  selected: boolean
  label: string
  suffix?: string
  suffixIntent?: 'default' | 'primary'
  onSelect: (value: RadioValue) => void
  body?: string
  value: RadioValue
}

export const RadioItem: React.FC<RadioItemProps> = ({
  selected,
  onSelect,
  label,
  suffix,
  suffixIntent = 'default',
  value,
  body,
  index
}) => (
  <StyledRadioItem
    custom={index}
    $isSelected={selected}
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    data-value={value}
    onClick={e => {
      e.preventDefault()
      onSelect(value)
    }}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        onSelect(value)
      }
    }}
  >
    <StyledRadioItemRow>
      <CheckMarkWrapper>
        <AnimatePresence mode="wait">
          {selected ? (
            <AnimatedRadioIcon key="check-icon" className="check">
              <FaCheckCircle />
            </AnimatedRadioIcon>
          ) : (
            <AnimatedRadioIcon key="unchecked-icon" className="uncheck">
              <FaRegCircle />
            </AnimatedRadioIcon>
          )}
        </AnimatePresence>
      </CheckMarkWrapper>
      <RadioButtonText $isSelected={selected}>{label}</RadioButtonText>
      {suffix && <RadioButtonSuffix $intent={suffixIntent}>{suffix}</RadioButtonSuffix>}
    </StyledRadioItemRow>
    {body && (
      <StyledRadioItemRow>
        <RadioButtonBody>{body}</RadioButtonBody>
      </StyledRadioItemRow>
    )}
  </StyledRadioItem>
)

const StyledRadioItem = styled(motion.div).attrs({
  variants: {
    hide: {
      y: -40,
      opacity: 0
    },
    show: {
      y: 0,
      opacity: 1
    }
  }
})<{ $isSelected: boolean }>`
  background: transparent;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  ${({ $isSelected, theme }) =>
    $isSelected &&
    `
    background: ${theme.colors.defaultHover};
  `}
`

const StyledRadioItemRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: center;
`

const CheckMarkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
`

const AnimatedRadioIcon = styled(motion.div).attrs({
  initial: { opacity: 0, scale: 0.5 },
  animate: { opacity: 1, scale: 1.0 },
  exit: { opacity: 0, scale: 0.5 },
  transition: { duration: 0.15 }
})`
  svg {
    width: 1.2rem;
    height: 1.2rem;
    fill: ${({ theme, className }) =>
      className === 'check' ? theme.colors.success : theme.colors.neutralGray};
  }
`
